import React, { ReactElement, useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { ArtData } from '@/database/art'
import { Context } from '@/components/Context'
import CheckBox from '@/components/boxes/CheckBox'
import serverConfig from '@/server-config'
import MarketCard from '@/components/market/MarketCard'
import MyMarketCard from '@/components/market/MyMarketCard'
import { Transaction } from '@/database/transaction'
import { ArtState, TransactionType } from '@/database/types'
import { SpecificUpdate, UpdateSpecific, UpdateSpecificType, UpdateType } from '@/types/websocket'

axios.defaults.baseURL = serverConfig.baseURL

const filterBids = (transactions: Transaction[]) =>
    transactions?.filter((transaction: Transaction) => {
        return transaction.type === TransactionType.OFFER
    })

const ListingOverview = ({
    openModalMarketCardDetails,
    openModalMyMarketCardDetails,
}: {
    openModalMarketCardDetails: (artData: ArtData) => void
    openModalMyMarketCardDetails: (artData: ArtData) => void
}): ReactElement => {
    const {
        artDataList,
        currentAccount,
        setActiveUpdates,
        setActiveSpecificUpdates,
        transactionUserArray,
        filters,
        toggleFilter,
    } = useContext(Context)
    const [bids, setBids] = useState(filterBids(transactionUserArray[currentAccount ?? ''] as Transaction[]))

    const filteredArt = (artDataList: ArtData[]) =>
        artDataList.filter((artData) => {
            const { forSale, offerings, marketMyArt, marketMyClaims, marketMyOffers } = filters
            if (artData.art.state === ArtState.AUCTION || artData.art.state === ArtState.FORCED_AUCTION) {
                return false
            }
            if (marketMyArt) {
                if (
                    !forSale &&
                    !marketMyClaims &&
                    !marketMyOffers &&
                    !offerings &&
                    (artData.art.user.walletAddress === currentAccount || artData.art.currentOwner === currentAccount)
                ) {
                    return true
                }
                if (
                    !(artData.art.user.walletAddress === currentAccount || artData.art.currentOwner === currentAccount)
                ) {
                    return false
                }
                if (forSale && artData.art.state === ArtState.FOR_SALE) {
                    return true
                }
                if (
                    marketMyClaims &&
                    artData.votes.some((voteData) => voteData.user.walletAddress === currentAccount)
                ) {
                    return true
                }
                return offerings && artData.art.state === ArtState.NOT_FOR_SALE
            }
            if (marketMyClaims) {
                if (artData.claims.some((claimData) => claimData.user.walletAddress === currentAccount)) {
                    if (forSale && artData.art.state === ArtState.FOR_SALE) {
                        return true
                    }
                    if (offerings && artData.art.state === ArtState.NOT_FOR_SALE) {
                        return true
                    }
                    return !forSale && !offerings
                }
                return false
            }
            if (marketMyOffers) {
                if (bids?.some((transaction) => artData.art.id === transaction.art.id)) {
                    if (!forSale && !offerings) {
                        return true
                    }
                    if (forSale && artData.art.state === ArtState.FOR_SALE) {
                        return true
                    }
                    return offerings && artData.art.state === ArtState.NOT_FOR_SALE
                }
                return false
            }
            if (forSale && artData.art.state === ArtState.FOR_SALE) {
                return true
            }
            if (offerings && artData.art.state === ArtState.NOT_FOR_SALE) {
                return true
            }
            return !forSale && !offerings && !marketMyArt
        })

    useEffect(() => {
        setBids(filterBids(transactionUserArray[currentAccount ?? ''] as Transaction[]))
    }, [transactionUserArray, currentAccount])

    useEffect(() => {
        setActiveSpecificUpdates({
            updates: [
                {
                    type: UpdateSpecificType.TRANSACTION_USER,
                    id: currentAccount,
                } as UpdateSpecific,
            ],
        } as SpecificUpdate)
    }, [transactionUserArray[currentAccount ?? ''], currentAccount])

    useEffect(() => {
        setActiveUpdates([UpdateType.MARKETS])
        setActiveSpecificUpdates({
            updates: [
                {
                    type: UpdateSpecificType.TRANSACTION_USER,
                    id: currentAccount,
                } as UpdateSpecific,
            ],
        } as SpecificUpdate)
    }, [])

    return (
        <div>
            <h1 className="title">Fixed Price Listing</h1>
            <p className="padding-bottom">
                <CheckBox
                    btnName="For&nbsp;Sale"
                    classStyles=""
                    checked={filters.forSale}
                    handleClick={() => toggleFilter('forSale')}
                />
                <CheckBox
                    btnName="Offerings"
                    classStyles=""
                    checked={filters.offerings}
                    handleClick={() => toggleFilter('offerings')}
                />
                <CheckBox
                    btnName="My&nbsp;Art"
                    classStyles=""
                    checked={filters.marketMyArt}
                    handleClick={() => toggleFilter('marketMyArt')}
                />
                <CheckBox
                    btnName="My&nbsp;Offers"
                    classStyles=""
                    checked={filters.marketMyOffers}
                    handleClick={() => toggleFilter('marketMyOffers')}
                />
                <CheckBox
                    btnName="My&nbsp;Claims"
                    classStyles=""
                    checked={filters.marketMyClaims}
                    handleClick={() => toggleFilter('marketMyClaims')}
                />
            </p>
            <div className="container">
                <div className="prompts">
                    <ul>
                        {filteredArt(artDataList).map((artData) => {
                            if (artData.art.currentOwner === currentAccount) {
                                return (
                                    <MyMarketCard
                                        key={artData.art.id}
                                        artData={artData}
                                        openModalMyMarketCardDetails={openModalMyMarketCardDetails}
                                    />
                                )
                            } else {
                                return (
                                    <MarketCard
                                        key={artData.art.id}
                                        artData={artData}
                                        openModalMarketCardDetails={openModalMarketCardDetails}
                                    />
                                )
                            }
                        })}
                    </ul>
                </div>
            </div>
        </div>
    )
}

ListingOverview.whyDidYouRender = true

export default ListingOverview
